'use client';

import { useEffect, useRef, useState } from 'react';
import {
  LegacyDialog,
  Button,
  Text,
  Switch,
  IconButton,
  toasterApi,
} from '@terra/ui-design-system';
import { VStack, HStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  ApplicationCredentialSelect,
  ApplicationCredentialSelectLabel,
} from './ApplicationCredentialSelect';

export type ConfigureCredentialsResult = {
  credentialId?: string;
  enableTracker: boolean;
};
type ConfigureCredentialsDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCredentialId?: string;
  onConfirm: (result: ConfigureCredentialsResult) => void | Promise<void>;
};

export function ConfigureCredentialsDialog({
  isOpen,
  onOpenChange,
  defaultCredentialId = '',
  onConfirm,
}: ConfigureCredentialsDialogProps) {
  const t = useTranslations('proxy.tracking.configureCredentialsDialog');
  const tCommon = useTranslations('common');
  const [selectedCredentialId, setSelectedCredentialId] =
    useState(defaultCredentialId);
  const [enableTracker, setEnableTracker] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedCredentialId(defaultCredentialId);
      setEnableTracker(false);
      setIsConfirming(false);
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, defaultCredentialId]);
  const handleConfirm = async () => {
    if (isConfirming) {
      return;
    }

    setIsConfirming(true);

    try {
      await onConfirm({
        credentialId: selectedCredentialId || undefined,
        enableTracker,
      });
    } catch (error: unknown) {
      toasterApi.error({
        title: t('trackRequestFailedTitle'),
        description:
          error instanceof Error
            ? error.message
            : t('trackRequestFailedDescription'),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open && isConfirming) {
          return;
        }

        onOpenChange(open);
      }}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="md" w="full">
          <VStack gap="4" p="4">
            <VStack gap="1" w="full" alignItems="flex-start">
              <LegacyDialog.Title>{t('title')}</LegacyDialog.Title>
              <LegacyDialog.Description color="fg.muted">
                {t('description')}
              </LegacyDialog.Description>
            </VStack>

            <VStack gap="2" w="full" alignItems="flex-start">
              <ApplicationCredentialSelectLabel />
              <ApplicationCredentialSelect
                value={selectedCredentialId}
                onValueChange={setSelectedCredentialId}
                skip={!isOpen}
              />
            </VStack>

            <HStack
              gap="2"
              w="full"
              alignItems="center"
              justifyContent="space-between"
            >
              <VStack gap="0" alignItems="flex-start">
                <Text fontSize="sm" fontWeight="medium">
                  {t('enableTracker')}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {t('enableTrackerDescription')}
                </Text>
              </VStack>
              <Switch
                checked={enableTracker}
                disabled={isConfirming}
                onCheckedChange={(checked) =>
                  setEnableTracker(!!checked.checked)
                }
              />
            </HStack>

            <HStack gap="2" w="full" justifyContent="flex-end">
              <LegacyDialog.CloseTrigger asChild>
                <Button variant="outline" size="xs" disabled={isConfirming}>
                  {tCommon('cancel')}
                </Button>
              </LegacyDialog.CloseTrigger>
              <Button
                size="xs"
                disabled={isConfirming}
                onClick={() => void handleConfirm()}
              >
                {t('trackRequest')}
              </Button>
            </HStack>
          </VStack>

          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton
              aria-label={t('closeAriaLabel')}
              variant="ghost"
              size="xs"
              disabled={isConfirming}
            >
              <Cancel01Icon size={14} />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
